import React, { createContext, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useGetCustomersWithAdminUserPermission } from 'src/api/customer'
import { axios } from 'src/axios'
import { CenteredSpinner } from 'src/components/Spinner'
import { SystemPortal } from 'src/enums'
import { useGetMe } from '../api/user'
import { AppRoutesNavigation } from '../app-routes'
import { UserStatus } from '../enums/user-status'
import { ICustomer, IUserWithPortalAccess } from '../generated-types'

interface UserContextProps {
  user: IUserWithPortalAccess
  isLoadingUser: boolean
  refetch: () => void
  activePortal: SystemPortal
  setActivePortal: (portal: SystemPortal) => void
  userCustomers: ICustomer[]
}

export const UserCtx = createContext<UserContextProps>(null!)

export const UserContextProvider: React.FC = ({ children }) => {
  const { data, isLoading, refetch, error } = useGetMe()
  const [isNavigated, setIsNavigated] = useState(false)
  const [activePortal, setActivePortal] = useState<SystemPortal>(SystemPortal.Locality)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { data: userCustomers, isLoading: isLoadingUserCustomers } = useGetCustomersWithAdminUserPermission()

  const timeOut = useRef<NodeJS.Timeout | undefined>()

  useEffect(() => {
    if (data) {
      axios.defaults.headers['X-User'] = data.encryptedUserId
    }
  }, [data])

  useEffect(() => {
    if (data) {
      const storedCustomerId = localStorage.getItem('selectedCustomerAdminCustomerId')
      const hasStoredCustomer = userCustomers?.find(c => c.id.toString() === storedCustomerId)
      if (data.customer && !hasStoredCustomer)
        localStorage.setItem('selectedCustomerAdminCustomerId', data.customer.id.toString())
    }
  }, [userCustomers, data])

  useEffect(() => {
    if (data == undefined && !isLoading && error != null) {
      navigate(AppRoutesNavigation.UnAuth)
    } else if (!isLoading && data) {
      const pathParts = pathname.split('/')
      const currentSection = pathParts[1]

      if (['locality', 'service-station', 'storage', 'project', ''].includes(currentSection) && pathParts.length <= 2) {
        // Try to stay in the current section if user has access
        if (currentSection === 'service-station' && data.hasServiceStationAccess) {
          navigate(AppRoutesNavigation.serviceStation.main)
        } else if (currentSection === 'storage' && data.hasStorageAccess) {
          navigate(AppRoutesNavigation.storage.main)
        } else if (currentSection === 'locality' && data.hasLocalityAccess) {
          navigate(AppRoutesNavigation.Localities.main)
        } else if (currentSection === 'project' && data.hasProjectPlanningAccess) {
          navigate(AppRoutesNavigation.Project.main)
        } else {
          // Fallback to any section they have access to
          if (data.hasLocalityAccess) {
            navigate(AppRoutesNavigation.Localities.main)
          } else if (data.hasServiceStationAccess) {
            navigate(AppRoutesNavigation.serviceStation.main)
          } else if (data.hasStorageAccess) {
            navigate(AppRoutesNavigation.storage.main)
          } else if (data.hasProjectPlanningAccess) {
            navigate(AppRoutesNavigation.Project.main)
          } else if (
            userCustomers &&
            userCustomers?.length > 0 &&
            !(userCustomers[0] as ICustomer).isGlobalServiceStationProvider
          ) {
            // if user doesn't have access to any section but has access to a customer admin main components page , navigate to that customer admin page
            navigate(AppRoutesNavigation.Customer.localities((userCustomers[0] as ICustomer).id))
          } else {
            navigate(AppRoutesNavigation.UnAuth)
          }
        }
      } else {
        // Check if user has no access to main sections
        const hasNoMainAccess = !data.hasLocalityAccess && !data.hasServiceStationAccess && !data.hasStorageAccess

        if (hasNoMainAccess) {
          // If they have customer admin access, redirect there
          if (
            userCustomers &&
            userCustomers.length > 0 &&
            (userCustomers[0] as ICustomer).isGlobalServiceStationProvider === false
          ) {
            navigate(AppRoutesNavigation.Customer.localities((userCustomers[0] as ICustomer).id))
          } else {
            // No main access and no customer admin access - unauthorized
            navigate(AppRoutesNavigation.UnAuth)
          }
        }
        // Users with access can navigate to other routes freely
      }
    }
  }, [data, isLoading, error, userCustomers])

  useEffect(() => {
    return () => {
      if (timeOut.current) {
        clearTimeout(timeOut.current)
      }
    }
  }, [])

  const contextValue: UserContextProps = {
    user: data!,
    isLoadingUser: isLoading,
    refetch,
    activePortal,
    setActivePortal,
    userCustomers: userCustomers ?? [],
  }

  if (isLoading || isLoadingUserCustomers) return <CenteredSpinner />

  if (!isLoading && data?.status == UserStatus.InComplete && !isNavigated && pathname !== AppRoutesNavigation.Profile) {
    timeOut.current = setTimeout(() => {
      setIsNavigated(true)
    }, 100)

    return <Navigate to={AppRoutesNavigation.Profile} state={{ prevPath: window.location.pathname }} />
  }

  return <UserCtx.Provider value={contextValue}>{children}</UserCtx.Provider>
}
